// src/components/property/SceneVideoThumbnail.tsx
/**
 * SceneVideoThumbnail Component
 *
 * Displays video preview with different states:
 * - Pending: Gray dashed border, video icon, "Not generated" text
 * - Generating: Skeleton animation with spinner
 * - Completed: Video player with play/expand controls on hover
 * - Failed: Red border, error icon, "Failed" text
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Play, Pause, Maximize2, Video, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { ISceneVideoThumbnailProps } from '@/@types/propertySceneEditor';

function SceneVideoThumbnail({
  videoStorageId,
  status,
  aspectRatio,
  size,
  hasExistingVideo: _hasExistingVideo, // Reserved for future use
  isRegenerating,
  onPlay,
  onExpand,
  onRegenerate,
}: ISceneVideoThumbnailProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Fetch video URL only when we have a storage ID
  const videoUrl = useQuery(
    api.storage.getUrl,
    videoStorageId ? { storageId: videoStorageId } : 'skip'
  );

  // Size classes based on aspect ratio - 1.5x larger
  const sizeClasses =
    aspectRatio === '9:16'
      ? size === 'small'
        ? 'w-[135px] h-[240px]'
        : 'w-[270px] h-[480px]'
      : size === 'small'
        ? 'w-[240px] h-[135px]'
        : 'w-[480px] h-[270px]';

  // Handle play/pause toggle
  const handlePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
    onPlay();
  }, [isPlaying, onPlay]);

  // Handle video ended
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleEnded = () => {
      setIsPlaying(false);
    };

    video.addEventListener('ended', handleEnded);
    return () => {
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  // Note: When status changes away from 'completed', the component renders
  // a different branch entirely (pending/generating/failed UI), so the video
  // element is unmounted. No need to manually pause - React cleanup handles it.

  // Render pending state
  if (status === 'pending') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              sizeClasses,
              'relative flex flex-col items-center justify-center gap-2',
              'border-2 border-dashed border-muted-foreground/30 rounded-lg',
              'bg-muted/50 text-muted-foreground'
            )}
          >
            <Video className="h-6 w-6" />
            <span className="text-xs font-medium">Not generated</span>
            <Button
              variant="secondary"
              size="sm"
              className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs"
              onClick={e => {
                e.stopPropagation();
                onRegenerate();
              }}
              disabled={isRegenerating}
            >
              {isRegenerating ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-3 w-3 mr-1" />
              )}
              Generate
            </Button>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Video not yet generated</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Render generating state
  if (status === 'generating') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              sizeClasses,
              'relative overflow-hidden rounded-lg',
              'bg-muted animate-pulse'
            )}
          >
            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
            {/* Centered spinner */}
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Generating video...</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Render failed state
  if (status === 'failed') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              sizeClasses,
              'relative flex flex-col items-center justify-center gap-2',
              'border-2 border-red-500/50 rounded-lg',
              'bg-red-500/10 text-red-500'
            )}
          >
            <AlertCircle className="h-6 w-6" />
            <span className="text-xs font-medium">Failed</span>
            <Button
              variant="secondary"
              size="sm"
              className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs"
              onClick={e => {
                e.stopPropagation();
                onRegenerate();
              }}
              disabled={isRegenerating}
            >
              {isRegenerating ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-3 w-3 mr-1" />
              )}
              Regenerate
            </Button>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Video generation failed</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Render completed state with video player
  return (
    <div
      className={cn(
        sizeClasses,
        'relative overflow-hidden rounded-lg',
        'bg-black group cursor-pointer'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Video element */}
      {videoUrl ? (
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full object-cover"
          muted
          playsInline
          loop={false}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-muted">
          <Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
        </div>
      )}

      {/* Hover overlay with controls */}
      <div
        className={cn(
          'absolute inset-0 bg-black/40 transition-opacity duration-200',
          'flex flex-col items-center justify-center',
          isHovered || !isPlaying ? 'opacity-100' : 'opacity-0'
        )}
      >
        {/* Play/Pause and Expand buttons */}
        <div className="flex items-center justify-center gap-3">
          {/* Play/Pause button */}
          <button
            onClick={handlePlayPause}
            className={cn(
              'p-2 rounded-full bg-white/20 hover:bg-white/30',
              'text-white transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-white/50'
            )}
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
          >
            {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
          </button>

          {/* Expand button */}
          <button
            onClick={e => {
              e.stopPropagation();
              onExpand();
            }}
            className={cn(
              'p-2 rounded-full bg-white/20 hover:bg-white/30',
              'text-white transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-white/50'
            )}
            aria-label="Expand video"
          >
            <Maximize2 className="h-5 w-5" />
          </button>
        </div>

        {/* Regenerate button at bottom */}
        <Button
          variant="secondary"
          size="sm"
          className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs"
          onClick={e => {
            e.stopPropagation();
            onRegenerate();
          }}
          disabled={isRegenerating}
        >
          {isRegenerating ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : (
            <RefreshCw className="h-3 w-3 mr-1" />
          )}
          Regenerate
        </Button>
      </div>

      {/* Playing indicator */}
      {isPlaying && (
        <div className="absolute bottom-2 left-2">
          <div className="flex items-center gap-1 px-2 py-1 rounded bg-black/70 text-white text-xs">
            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            Playing
          </div>
        </div>
      )}
    </div>
  );
}

export { SceneVideoThumbnail };
